import React from "react";
import PropTypes from "prop-types";
import { Collapse, Badge } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { withTranslation, Trans } from "react-i18next";

import "./Sidebar.css";
import SidebarRun from "./Sidebar.run";

import Menu from "../../../../Menu";
import { useEffect, useState } from "react";
import useUser from "../../../hooks/use-user";
import { BASE_PATH } from "../../../config/env";

/** Component to display headings on sidebar */
const SidebarItemHeader = ({ item }) => (
    <li className="nav-heading">
        <span>
            <Trans i18nKey={item.translate}>{item.heading}</Trans>
        </span>
    </li>
);

SidebarItemHeader.propTypes = {
    item: PropTypes.object,
};

/** Normal items for the sidebar */
const SidebarItem = ({ item, isActive }) => {
    const path =
        BASE_PATH != "/" && item.path.includes(BASE_PATH)
            ? item.path.replace(BASE_PATH, "")
            : item.path;

    return (
        <li className={isActive ? "active" : ""}>
            <Link to={path} title={item.name}>
                {item.label && (
                    <Badge
                        tag="div"
                        className="float-right"
                        color={item.label.color}
                    >
                        {item.label.value}
                    </Badge>
                )}
                {item.icon && <em className={item.icon}></em>}
                <span>
                    <Trans i18nKey={item.translate}>{item.name}</Trans>
                </span>
            </Link>
        </li>
    );
};

SidebarItem.propTypes = {
    item: PropTypes.object,
    isActive: PropTypes.bool,
};

/** Build a sub menu with items inside and attach collapse behavior */
const SidebarSubItem = ({ item, isActive, handler, children, isOpen }) => (
    <li className={isActive ? "active" : ""}>
        <div className="nav-item" onClick={handler}>
            {item.label && (
                <Badge
                    tag="div"
                    className="float-right"
                    color={item.label.color}
                >
                    {item.label.value}
                </Badge>
            )}
            {item.icon && <em className={item.icon}></em>}
            <span>
                <Trans i18nKey={item.translate}>{item.name}</Trans>
            </span>
        </div>
        <Collapse isOpen={isOpen}>
            <ul id={item.path} className="sidebar-nav sidebar-subnav">
                {children}
            </ul>
        </Collapse>
    </li>
);

SidebarSubItem.propTypes = {
    item: PropTypes.object,
    isActive: PropTypes.bool,
    handler: PropTypes.func,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    isOpen: PropTypes.bool,
};

/** Component used to display a header on menu when using collapsed/hover mode */
const SidebarSubHeader = ({ item }) => (
    <li className="sidebar-subnav-header">{item.name}</li>
);

SidebarSubHeader.propTypes = {
    item: PropTypes.object,
};

const Sidebar = function (props) {
    const [state, setState] = useState({
        collapse: {},
    });
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const { getUserData, isLoggedIn } = useUser();
    const userData = getUserData();

    const navigate = useNavigate();

    // Añade o borra una clase a un elemento dependiendo si su stat es true
    const updateElementClass = (el, stat, name) =>
        el && el.classList[stat ? "add" : "remove"](name);
    // Actualiza clases del body dependiendo de la configuración especificada
    const updateBodyClass = (stat, name) =>
        updateElementClass(document.body, stat, name);

    const closeSidebar = () => {
        updateBodyClass(false, "aside-collapsed");
    };

    /** prepare initial state of collapse menus. Doesnt allow same route names */
    const buildCollapseList = () => {
        let collapse = {};
        Menu.filter(({ heading }) => !heading).forEach(
            ({ name, path, submenu }) => {
                collapse[name] = routeActive(
                    submenu ? submenu.map(({ path }) => path) : path
                );
            }
        );
        setState({ collapse });
    };

    const shouldRenderSubitem = (subitem) => {
        if (!subitem.permissions) return true;
        if (!isLoggedIn) return false;

        if (typeof subitem.permissions === "string") {
            return userData.permisos.includes(subitem.permissions);
        }

        if (Array.isArray(subitem.permissions)) {
            return userData.permisos.some((permiso) =>
                subitem.permissions.includes(permiso)
            );
        }

        return false;
    };

    const navigator = (route) => {
        navigate(route.replace("#", "")); // remove '#' in case of use HashRouter
    };

    const routeActive = function (paths) {
        paths = Array.isArray(paths) ? paths : [paths];
        return paths.some((p) => props.location.pathname.indexOf(p) > -1);
    };

    const toggleItemCollapse = function (stateName) {
        // eslint-disable-next-line
        for (let c in state.collapse) {
            if (state.collapse[c] === true && c !== stateName)
                setState({
                    collapse: {
                        [c]: false,
                    },
                });
        }

        setState({
            collapse: {
                [stateName]: !state.collapse[stateName],
            },
        });
    };

    const getSubRoutes = (item) => item.submenu.map(({ path }) => path);

    /** map menu config to string to determine which element to render */
    const itemType = (item) => {
        if (item.heading) return "heading";
        if (!item.submenu) return "menu";
        if (item.submenu) return "submenu";
    };

    const resize = () => {
        const evt = document.createEvent("UIEvents");
        evt.initUIEvent("resize", true, false, window, 0);
        window.dispatchEvent(evt);
    };

    const toggleCollapsed = (e) => {
        e.preventDefault();
        updateBodyClass(!isSidebarCollapsed, "aside-collapsed");
        setIsSidebarCollapsed(!isSidebarCollapsed);
        resize();
    };

    useEffect(() => {
        // pass navigator to access router api
        SidebarRun(navigator, closeSidebar);
        // prepare the flags to handle menu collapsed states
        buildCollapseList();
    }, []);

    return (
        <div>
            <aside className="aside-container">
                {/* START Sidebar (left) */}
                <div className="aside-inner">
                    <nav data-sidebar-anyclick-close="" className="sidebar">
                        {/* START sidebar nav */}
                        <ul className="sidebar-nav">
                            {/* Iterates over all sidebar items */}
                            {Menu.map((item, i) => {
                                // heading
                                if (
                                    item.permissions != null &&
                                    isLoggedIn &&
                                    !userData.permisos.some((permiso) =>
                                        item.permissions.includes(permiso)
                                    )
                                ) {
                                    return null;
                                } else if (itemType(item) === "heading")
                                    return (
                                        <SidebarItemHeader
                                            item={item}
                                            key={i}
                                        />
                                    );
                                else {
                                    if (itemType(item) === "menu")
                                        return (
                                            <SidebarItem
                                                isActive={routeActive(
                                                    item.path
                                                )}
                                                item={item}
                                                key={i}
                                            />
                                        );
                                    if (itemType(item) === "submenu")
                                        return [
                                            <SidebarSubItem
                                                item={item}
                                                isOpen={
                                                    state.collapse[item.name]
                                                }
                                                handler={toggleItemCollapse.bind(
                                                    this,
                                                    item.name
                                                )}
                                                isActive={routeActive(
                                                    getSubRoutes(item)
                                                )}
                                                key={i}
                                            >
                                                <SidebarSubHeader
                                                    item={item}
                                                    key={i}
                                                />
                                                {item.submenu
                                                    .filter(shouldRenderSubitem)
                                                    .map((subitem, i) => {
                                                        return (
                                                            <SidebarItem
                                                                key={i}
                                                                item={subitem}
                                                                isActive={routeActive(
                                                                    subitem.path
                                                                )}
                                                            />
                                                        );
                                                    })}
                                            </SidebarSubItem>,
                                        ];
                                }
                                return null; // unrecognized item
                            })}
                        </ul>
                        {/* END sidebar nav */}
                    </nav>
                    <div
                        className="menu-toggler text-white rounded-circle bg-secondary d-none d-lg-flex d-xl-flex d-md-flex"
                        onClick={toggleCollapsed}
                    >
                        <i
                            className={`fas ${
                                isSidebarCollapsed
                                    ? "fa-angle-right"
                                    : "fa-angle-left"
                            }`}
                        ></i>
                    </div>
                </div>
                {/* END Sidebar (left) */}
            </aside>
        </div>
    );
};

Sidebar.propTypes = {
    location: PropTypes.object,
};

export default withTranslation("translations")(Sidebar);
